import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Box,
  Alert,
  Dialog,
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Grid,
  LinearProgress,
  Tooltip,
  Checkbox,
  TablePagination,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  InputAdornment
} from '@mui/material';
import { 
  Star as StarIcon, 
  StarBorder as StarBorderIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Add as AddIcon,
  FileUpload as ImportIcon,
  FileDownload as DownloadIcon,
  Help as HelpIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import api from '../services/api';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import PuzzleVerificationDialog from '../components/PuzzleVerificationDialog';

const PlacementManager = () => {
  const [placements, setPlacements] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [schools, setSchools] = useState([]);
  const [students, setStudents] = useState([]);
  const [coTeachers, setCoTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [puzzleOpen, setPuzzleOpen] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [currentSemester, setCurrentSemester] = useState<number>(1);
  const [currentAcademicYear, setCurrentAcademicYear] = useState<number>(new Date().getFullYear() + 543);
  const [filterSemester, setFilterSemester] = useState<string>('ALL');
  const [filterAcademicYear, setFilterAcademicYear] = useState<string>('ALL');
  const [filterSchool, setFilterSchool] = useState<string>('ALL');
  const [filterCourse, setFilterCourse] = useState<string>('ALL');
  const [initialized, setInitialized] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [message, setMessage] = useState({ type: 'info', text: '' });
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importMessage, setImportMessage] = useState({ type: 'info', text: '' });
  const [importErrors, setImportErrors] = useState<any[]>([]);
  const [selectedPlacement, setSelectedPlacement] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkCourseOpen, setBulkCourseOpen] = useState(false);
  const [bulkCourseId, setBulkCourseId] = useState('');
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [sectionSemester, setSectionSemester] = useState<number>(1);
  const [sectionAcademicYear, setSectionAcademicYear] = useState<number>(new Date().getFullYear() + 543);
  const [sectionGroupNo, setSectionGroupNo] = useState<string>('');
  const [sectionCourseId, setSectionCourseId] = useState<string>('');
  const [sectionSchoolId, setSectionSchoolId] = useState<string>('');
  const [sectionProfSupId, setSectionProfSupId] = useState<string>('');
  const [sectionSpecSupId, setSectionSpecSupId] = useState<string>('');
  const [bulkSchoolOpen, setBulkSchoolOpen] = useState(false);
  const [bulkSchoolId, setBulkSchoolId] = useState('');
  
  const [formData, setFormData] = useState({
    student_id: '',
    school_id: '',
    course_id: '',
    prof_supervisor_id: '',
    spec_supervisor_id: '',
    primary_spec_id: '',
    co_teacher_id: '',
    semester: 1,
    academic_year: new Date().getFullYear() + 543,
    status: 'PENDING'
  });

  const uniqueGroupOptions = React.useMemo(() => {
    const groupsMap = new Map<string, string>();
    students.forEach((s: any) => {
      if (s.group_no && s.group_no.trim() !== '') {
        if (!groupsMap.has(s.group_no) || (s.major && s.major.trim() !== '')) {
          groupsMap.set(s.group_no, s.major || '');
        }
      }
    });
    return Array.from(groupsMap.entries())
      .map(([group_no, major]) => ({
        group_no,
        major,
        label: major ? `${group_no} - ${major}` : group_no
      }))
      .sort((a, b) => a.group_no.localeCompare(b.group_no));
  }, [students]);

  const studentsInSection = React.useMemo(() => {
    if (!sectionGroupNo) return [];
    return students.filter((s: any) => s.group_no === sectionGroupNo);
  }, [students, sectionGroupNo]);

  const courseStats = React.useMemo(() => {
    const stats: Record<string, { total_students: number, groups: { group_no: string, student_count: number }[] }> = {};
    placements.forEach((p: any) => {
      if (filterAcademicYear !== 'ALL' && String(p.academic_year) !== String(filterAcademicYear)) return;
      if (filterSemester !== 'ALL' && String(p.semester) !== String(filterSemester)) return;
      const cId = p.course_id;
      if (!cId) return;
      if (!stats[cId]) {
        stats[cId] = { total_students: 0, groups: [] };
      }
      stats[cId].total_students += 1;
      const gNo = (p.group_no && p.group_no.trim()) || 'ไม่ระบุหมู่เรียน';
      let g = stats[cId].groups.find(x => x.group_no === gNo);
      if (!g) {
        g = { group_no: gNo, student_count: 0 };
        stats[cId].groups.push(g);
      }
      g.student_count += 1;
    });

    Object.values(stats).forEach(s => {
      s.groups.sort((a, b) => a.group_no.localeCompare(b.group_no));
    });
    return stats;
  }, [placements, filterAcademicYear, filterSemester]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pRes, sRes, schRes, ctRes, stdRes, cRes, setRes] = await Promise.all([
        api.get('/placements'),
        api.get('/supervisors'),
        api.get('/schools'),
        api.get('/co-teachers'),
        api.get('/students'),
        api.get('/courses'),
        api.get('/settings')
      ]);
      setPlacements(pRes.data);
      setSupervisors(sRes.data);
      setSchools(schRes.data);
      setCoTeachers(ctRes.data);
      setStudents(stdRes.data);
      setCourses(cRes.data);

      const settings = setRes.data || {};
      const defSem = settings.current_semester ? Number(settings.current_semester) : 1;
      const defYear = settings.current_academic_year ? Number(settings.current_academic_year) : (new Date().getFullYear() + 543);
      setCurrentSemester(defSem);
      setCurrentAcademicYear(defYear);
      setSectionSemester(defSem);
      setSectionAcademicYear(defYear);

      if (!initialized) {
        setFilterSemester(String(defSem));
        setFilterAcademicYear(String(defYear));
        setInitialized(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = filteredPlacements.map((n: any) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleSelectOne = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`ยืนยันการย้ายข้อมูลการจัดสรรที่เลือก ${selected.length} รายการ ไปยังถังขยะ?`)) {
      setLoading(true);
      try {
        await api.post('/placements/bulk-soft-delete', { ids: selected });
        setSelected([]);
        setMessage({ type: 'success', text: 'ย้ายข้อมูลไปยังถังขยะเรียบร้อยแล้ว' });
        fetchData();
      } catch (err) {
        console.error(err);
        alert('เกิดข้อผิดพลาดในการลบข้อมูล');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkEditCourse = async () => {
    if (selected.length === 0) {
      alert('กรุณาเลือกรายการที่ต้องการกำหนดรายวิชา');
      return;
    }
    if (!bulkCourseId) {
      alert('กรุณาเลือกรายวิชาที่จะกำหนด');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/placements/bulk-allocate', {
        semester: filterSemester !== 'ALL' ? Number(filterSemester) : currentSemester,
        academic_year: filterAcademicYear !== 'ALL' ? Number(filterAcademicYear) : currentAcademicYear,
        placement_ids: selected,
        course_id: bulkCourseId
      });
      alert(res.data.message || 'ปรับปรุงรายวิชาเรียบร้อยแล้ว');
      setBulkCourseOpen(false);
      setBulkCourseId('');
      setSelected([]);
      fetchData();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการกำหนดรายวิชา');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEditSchool = async () => {
    if (selected.length === 0) {
      alert('กรุณาเลือกรายการที่ต้องการกำหนดโรงเรียน');
      return;
    }
    if (!bulkSchoolId) {
      alert('กรุณาเลือกโรงเรียนที่จะกำหนด');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/placements/bulk-allocate', {
        semester: filterSemester !== 'ALL' ? Number(filterSemester) : currentSemester,
        academic_year: filterAcademicYear !== 'ALL' ? Number(filterAcademicYear) : currentAcademicYear,
        placement_ids: selected,
        school_id: bulkSchoolId
      });
      alert(res.data.message || 'ปรับปรุงโรงเรียนเรียบร้อยแล้ว');
      setBulkSchoolOpen(false);
      setBulkSchoolId('');
      setSelected([]);
      fetchData();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการกำหนดโรงเรียน');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAllocateBySection = async () => {
    if (!sectionGroupNo) {
      alert('กรุณาเลือกหมู่เรียน');
      return;
    }
    if (!sectionCourseId && !sectionSchoolId) {
      alert('กรุณาเลือกรายวิชาหรือโรงเรียนที่ต้องการจัดสรร');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/placements/bulk-allocate', {
        semester: sectionSemester,
        academic_year: sectionAcademicYear,
        group_no: sectionGroupNo,
        course_id: sectionCourseId || undefined,
        school_id: sectionSchoolId || undefined,
        prof_supervisor_id: sectionProfSupId || undefined,
        spec_supervisor_id: sectionSpecSupId || undefined
      });
      alert(res.data.message || 'จัดสรรข้อมูลตามหมู่เรียนเรียบร้อยแล้ว');
      setSectionDialogOpen(false);
      setSectionGroupNo('');
      setSectionCourseId('');
      setSectionSchoolId('');
      setSectionProfSupId('');
      setSectionSpecSupId('');
      fetchData();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการจัดสรรข้อมูลตามหมู่เรียน');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoMatchClick = async () => {
    try {
      const settingsRes = await api.get('/settings');
      const { current_semester, current_academic_year } = settingsRes.data;
      if (current_semester) setCurrentSemester(parseInt(current_semester, 10));
      if (current_academic_year) setCurrentAcademicYear(parseInt(current_academic_year, 10));
    } catch (err) {
      console.error('Error fetching settings for auto-match:', err);
    }
    setPuzzleOpen(true);
  };

  const handleExecuteAutoMatch = async () => {
    setAutoMatching(true);
    try {
      const response = await api.post('/placements/auto-match', {
        semester: currentSemester,
        academic_year: currentAcademicYear
      });
      setPuzzleOpen(false);
      setMessage({ type: 'success', text: response.data.message });
      fetchData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'การจัดสรรอัตโนมัติล้มเหลว' });
    } finally {
      setAutoMatching(false);
    }
  };

  const handleOpenAdd = () => {
    setSelectedPlacement(null);
    setFormData({
      student_id: '',
      school_id: '',
      course_id: '',
      prof_supervisor_id: '',
      spec_supervisor_id: '',
      primary_spec_id: '',
      co_teacher_id: '',
      semester: 1,
      academic_year: new Date().getFullYear() + 543,
      status: 'PENDING'
    });
    setOpen(true);
  };

  const handleOpenEdit = (placement: any) => {
    setSelectedPlacement(placement);
    setFormData({
      student_id: placement.student_id,
      school_id: placement.school_id || '',
      course_id: placement.course_id || '',
      prof_supervisor_id: placement.prof_supervisor_id || '',
      spec_supervisor_id: placement.spec_supervisor_id || '',
      primary_spec_id: placement.primary_spec_id || placement.spec_supervisor_id || '',
      co_teacher_id: placement.co_teacher_id || '',
      semester: placement.semester || 1,
      academic_year: placement.academic_year || (new Date().getFullYear() + 543),
      status: placement.status || 'PENDING'
    });
    setOpen(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let finalData = { ...formData };
      if (!finalData.primary_spec_id && finalData.spec_supervisor_id) {
          finalData.primary_spec_id = finalData.spec_supervisor_id;
      }

      if (selectedPlacement) {
        await api.put(`/placements/${selectedPlacement.id}`, finalData);
        setMessage({ type: 'success', text: 'ปรับปรุงข้อมูลการจัดสรรเรียบร้อยแล้ว' });
      } else {
        await api.post('/placements', finalData);
        setMessage({ type: 'success', text: 'เพิ่มการจัดสรรใหม่เรียบร้อยแล้ว' });
      }
      setOpen(false);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || err.response?.data?.error || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('ยืนยันการลบรายการจัดสรรนี้? รายการจะถูกย้ายไปยังถังขยะ')) {
      setLoading(true);
      try {
        await api.delete(`/placements/${id}`);
        setMessage({ type: 'success', text: 'ย้ายรายการจัดสรรไปยังถังขยะแล้ว' });
        fetchData();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleFeatured = async (placementId: string, currentStatus: boolean) => {
    setLoading(true);
    try {
      await api.post(`/placements/${placementId}/feature`, { isFeatured: !currentStatus });
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const filteredPlacements = placements.filter((p: any) => {
    // 1. Semester filter
    if (filterSemester !== 'ALL' && String(p.semester) !== String(filterSemester)) {
      return false;
    }
    // 2. Academic year filter
    if (filterAcademicYear !== 'ALL' && String(p.academic_year) !== String(filterAcademicYear)) {
      return false;
    }
    // 3. School filter
    if (filterSchool !== 'ALL' && p.school_id !== filterSchool) {
      return false;
    }
    // 4. Course filter
    if (filterCourse !== 'ALL' && p.course_id !== filterCourse) {
      return false;
    }
    // 5. Search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchSearch = 
        p.student_first?.toLowerCase().includes(term) ||
        p.student_last?.toLowerCase().includes(term) ||
        p.student_code?.includes(term) ||
        p.school_name?.toLowerCase().includes(term) ||
        p.course_name?.toLowerCase().includes(term) ||
        p.course_code?.toLowerCase().includes(term) ||
        p.spec_supervisor_first?.toLowerCase().includes(term) ||
        p.prof_supervisor_first?.toLowerCase().includes(term) ||
        p.major?.toLowerCase().includes(term) ||
        p.group_no?.toLowerCase().includes(term);
      if (!matchSearch) return false;
    }
    return true;
  });

  useEffect(() => {
    setPage(0);
  }, [searchTerm, filterSemester, filterAcademicYear, filterSchool, filterCourse]);

  const handleFileUpload = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsedPlacements = results.data.map((row: any) => ({
          student_code: row['รหัสนักศึกษา'] || row['student_code'],
          school_name: row['ชื่อโรงเรียน'] || row['school_name'],
          course_code: row['รหัสวิชา'] || row['course_code'],
          prof_supervisor_email: row['Email อาจารย์วิชาชีพ'] || row['prof_supervisor_email'],
          spec_supervisor_email: row['Email อาจารย์วิชาเอก'] || row['Email อาจารย์วิชาเอก 1'] || row['spec_supervisor_email'],
          primary_spec_email: row['Email ผู้ประเมินหลัก'] || row['primary_spec_email'],
          semester: parseInt(row['เทอม'] || row['semester']) || 1,
          academic_year: parseInt(row['ปีการศึกษา'] || row['academic_year']) || (new Date().getFullYear() + 543),
          status: row['สถานะ'] || row['status'] || 'PENDING'
        }));

        try {
          setImportErrors([]); // Clear previous errors
          const res = await api.post('/placements/bulk', { placements: parsedPlacements });
          const successes = res.data.results.filter((r: any) => r.status === 'imported');
          const failures = res.data.results.filter((r: any) => r.status === 'failed');
          
          setImportErrors(failures);

          let resultText = `นำเข้าสำเร็จ ${successes.length} รายการ`;
          if (failures.length > 0) {
              resultText += `, ล้มเหลว ${failures.length} รายการ (โปรดตรวจสอบรายละเอียดด้านล่าง)`;
          }

          setImportMessage({ 
            type: failures.length > 0 ? 'warning' : 'success', 
            text: resultText 
          });
          fetchData();
        } catch (err: any) {
          setImportMessage({ type: 'error', text: err.response?.data?.message || 'การนำเข้าข้อมูลล้มเหลว' });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleExportExcel = () => {
    const exportData = filteredPlacements.map((p: any) => ({
      'รหัสนักศึกษา': p.student_code,
      'ชื่อ-นามสกุลนักศึกษา': `${p.student_first} ${p.student_last}`,
      'โรงเรียน': p.school_name,
      'รหัสวิชา': p.course_code,
      'ชื่อวิชา': p.course_name,
      'เทอม': p.semester,
      'ปีการศึกษา': p.academic_year,
      'อาจารย์นิเทศก์ (วิชาชีพ)': p.prof_supervisor_first ? `${p.prof_supervisor_prefix || ''}${p.prof_supervisor_first} ${p.prof_supervisor_last}`.trim() : '-',
      'อาจารย์นิเทศก์ (วิชาเอก)': p.spec_supervisor_first ? `${p.spec_supervisor_prefix || ''}${p.spec_supervisor_first} ${p.spec_supervisor_last}`.trim() : '-',
      'ครูพี่เลี้ยง': p.coteacher_first ? `${p.coteacher_first} ${p.coteacher_last}` : '-',
      'สถานะ': p.status
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Placements');
    XLSX.writeFile(workbook, `tpms_placements_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleDownloadTemplate = () => {
    const headers = ['รหัสนักศึกษา', 'ชื่อโรงเรียน', 'รหัสวิชา', 'Email อาจารย์วิชาชีพ', 'Email อาจารย์วิชาเอก', 'เทอม', 'ปีการศึกษา', 'สถานะ'];
    const sampleRow = ['661120101', 'โรงเรียนสาธิตฯ', 'EDU001', 'prof@gmail.com', 'spec@gmail.com', '1', '2569', 'ACTIVE'];

    const csvContent = Papa.unparse({
      fields: headers,
      data: [sampleRow]
    });

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'placement_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && <LinearProgress sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 2000 }} />}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' }, 
        gap: 2, 
        mb: 3 
      }}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
            COURSE & SCHOOL PLACEMENT MANAGEMENT
          </Typography>
          <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 800, color: '#0F172A' }}>
            การจัดสรรรายวิชาและโรงเรียนฝึกประสบการณ์
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<EditIcon />} 
            onClick={() => {
              setSectionGroupNo('');
              setSectionCourseId('');
              setSectionSchoolId('');
              setSectionProfSupId('');
              setSectionSpecSupId('');
              setSectionDialogOpen(true);
            }}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            จัดสรรตามหมู่เรียน (ทั้งหมู่)
          </Button>
          <Button 
            variant="outlined" 
            color="primary"
            startIcon={<AddIcon />} 
            onClick={handleOpenAdd}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            จัดสรรรายบุคคล
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={handleAutoMatchClick}
            sx={{ fontWeight: 600, borderRadius: 2 }}
          >
            ระบบจัดสรรอัตโนมัติ
          </Button>
          <Button 
            variant="outlined" 
            color="success"
            startIcon={<DownloadIcon />} 
            onClick={handleExportExcel}
            sx={{ borderRadius: 2 }}
          >
            ส่งออก Excel
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<ImportIcon />} 
            onClick={() => setImportOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            นำเข้า CSV
          </Button>
        </Box>
      </Box>

      {message.text && (
        <Alert severity={message.type as any} sx={{ mb: 2 }} onClose={() => setMessage({ ...message, text: '' })}>
          {message.text}
        </Alert>
      )}

      {/* Filter and Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          {/* Semester */}
          <Grid size={{ xs: 6, sm: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="placement-sem-label">ภาคเรียน</InputLabel>
              <Select
                labelId="placement-sem-label"
                value={filterSemester}
                label="ภาคเรียน"
                onChange={(e) => {
                  setFilterSemester(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">ทั้งหมด</MenuItem>
                <MenuItem value="1">ภาคเรียนที่ 1</MenuItem>
                <MenuItem value="2">ภาคเรียนที่ 2</MenuItem>
                <MenuItem value="3">ภาคเรียนที่ 3</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Academic Year */}
          <Grid size={{ xs: 6, sm: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="placement-year-label">ปีการศึกษา</InputLabel>
              <Select
                labelId="placement-year-label"
                value={filterAcademicYear}
                label="ปีการศึกษา"
                onChange={(e) => {
                  setFilterAcademicYear(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">ทั้งหมด</MenuItem>
                {['2569', '2568', '2567', '2566', '2565', '2564'].map(yr => (
                  <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* School */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="placement-school-label">โรงเรียน</InputLabel>
              <Select
                labelId="placement-school-label"
                value={filterSchool}
                label="โรงเรียน"
                renderValue={(selected) => {
                  if (selected === 'ALL') return 'ทุกโรงเรียน (ทั้งหมด)';
                  const found: any = schools.find((s: any) => s.id === selected);
                  return found ? found.name : selected;
                }}
                onChange={(e) => {
                  setFilterSchool(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">
                  <em>ทุกโรงเรียน (ทั้งหมด)</em>
                </MenuItem>
                {schools.map((s: any) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Course */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="placement-course-label">รายวิชาฝึกประสบการณ์</InputLabel>
              <Select
                labelId="placement-course-label"
                value={filterCourse}
                label="รายวิชาฝึกประสบการณ์"
                renderValue={(selected) => {
                  if (selected === 'ALL') return 'ทุกรายวิชา (ทั้งหมด)';
                  const found: any = courses.find((c: any) => c.id === selected);
                  return found ? `${found.course_code} ${found.course_name}` : selected;
                }}
                onChange={(e) => {
                  setFilterCourse(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">
                  <em>ทุกรายวิชา (ทั้งหมด)</em>
                </MenuItem>
                {courses.map((c: any) => {
                  const stat = courseStats[c.id];
                  const hasStudents = stat && stat.total_students > 0 && stat.groups && stat.groups.length > 0;
                  return (
                    <MenuItem key={c.id} value={c.id} sx={{ p: 0 }}>
                      <Tooltip
                        placement="right"
                        arrow
                        slotProps={{
                          tooltip: {
                            sx: { bgcolor: '#1e293b', boxShadow: 4, p: 1.5, minWidth: 220 }
                          }
                        }}
                        title={
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'white', borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 0.5, mb: 0.5 }}>
                              {c.course_code} {c.course_name}
                            </Typography>
                            {hasStudents ? (
                              <>
                                <Typography variant="caption" sx={{ display: 'block', color: '#93c5fd', fontWeight: 600, mb: 0.5 }}>
                                  การกระจายตามหมู่เรียน:
                                </Typography>
                                {stat.groups.map((g: any, gIdx: number) => (
                                  <Typography key={gIdx} variant="caption" sx={{ display: 'block', color: '#f1f5f9', pl: 0.5 }}>
                                    • หมู่เรียน {g.group_no}: <strong>{g.student_count}</strong> คน
                                  </Typography>
                                ))}
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#38bdf8', mt: 0.8, pt: 0.5, borderTop: '1px dashed rgba(255,255,255,0.2)' }}>
                                  รวมนักศึกษา: {stat.total_students} คน
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="caption" sx={{ color: '#f87171', fontWeight: 600, display: 'block' }}>
                                ไม่พบรายชื่อนักศึกษา
                              </Typography>
                            )}
                          </Box>
                        }
                      >
                        <Box sx={{ py: 1, px: 2, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {c.course_code} {c.course_name}
                          </Typography>
                          {hasStudents ? (
                            <Chip label={`${stat.total_students} คน`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', ml: 1, color: 'primary.main', borderColor: 'primary.light' }} />
                          ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', ml: 1 }}>
                              (0 คน)
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Search Bar */}
          <Grid size={{ xs: 12 }}>
            <TextField 
              fullWidth 
              size="small" 
              placeholder="ค้นหาชื่อนักศึกษา, รหัส, โรงเรียน, รายวิชา, อาจารย์นิเทศก์ หรือหมู่เรียน..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto', boxShadow: 2, borderRadius: 2 }}>
        {selected.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'primary.50', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 'bold' }}>
              เลือกรายการจัดสรรแล้ว {selected.length} รายการ
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<EditIcon />} 
                onClick={() => {
                  setBulkCourseId('');
                  setBulkCourseOpen(true);
                }}
                size="small"
              >
                แก้ไขรายวิชา
              </Button>
              <Button 
                variant="contained" 
                color="info" 
                startIcon={<EditIcon />} 
                onClick={() => {
                  setBulkSchoolId('');
                  setBulkSchoolOpen(true);
                }}
                size="small"
              >
                แก้ไขโรงเรียน
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                startIcon={<DeleteIcon />} 
                onClick={handleBulkDelete}
                size="small"
              >
                ลบรายการที่เลือก
              </Button>
            </Box>
          </Box>
        )}
        <Table sx={{ minWidth: 1100 }}>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < filteredPlacements.length}
                  checked={filteredPlacements.length > 0 && selected.length === filteredPlacements.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>นักศึกษา</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>รายวิชา</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>โรงเรียน</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>อาจารย์นิเทศก์ (วิชาชีพ / วิชาเอก)</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>เทอม/ปี</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>สถานะ</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>การจัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPlacements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((p: any) => {
              const isItemSelected = selected.indexOf(p.id) !== -1;
              return (
                <TableRow 
                  key={p.id} 
                  hover 
                  selected={isItemSelected}
                  onClick={() => handleSelectOne(p.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={isItemSelected} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{p.student_first} {p.student_last}</Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{p.student_code}</Typography>
                    {p.group_no && (
                      <Chip label={`หมู่เรียน: ${p.group_no}`} size="small" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{p.course_name || '-'}</Typography>
                    <Typography variant="caption" color="textSecondary">{p.course_code || ''}</Typography>
                  </TableCell>
                  <TableCell>{p.school_name}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        <b>Prof:</b> {p.prof_supervisor_first ? `${p.prof_supervisor_prefix || ''}${p.prof_supervisor_first} ${p.prof_supervisor_last}`.trim() : 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 'bold' }}>
                        <b>Spec:</b> {p.spec_supervisor_first ? `${p.spec_supervisor_prefix || ''}${p.spec_supervisor_first} ${p.spec_supervisor_last}`.trim() : 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{p.semester}/{p.academic_year}</TableCell>
                  <TableCell>
                    <Chip 
                      label={p.status} 
                      size="small" 
                      color={p.status === 'ACTIVE' ? 'success' : (p.status === 'COMPLETED' ? 'primary' : 'default')} 
                    />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title={p.is_featured ? 'เลิกโชว์ผลงาน' : 'โชว์ผลงานดีเด่น'}>
                        <IconButton 
                          onClick={() => handleToggleFeatured(p.id, p.is_featured)} 
                          color={p.is_featured ? 'warning' : 'default'}
                          size="small"
                        >
                          {p.is_featured ? <StarIcon /> : <StarBorderIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="แก้ไขข้อมูล">
                        <IconButton color="primary" size="small" onClick={() => handleOpenEdit(p)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="ลบรายการ">
                        <IconButton color="error" size="small" onClick={() => handleDelete(p.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredPlacements.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  ไม่พบข้อมูลการจัดสรรที่ตรงตามเงื่อนไข
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredPlacements.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="รายการต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
        />
      </TableContainer>

      {/* Import CSV Dialog */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImportIcon color="primary" /> นำเข้าข้อมูลการจัดสรรจากไฟล์ CSV
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: 'primary.main' }}>
              <HelpIcon fontSize="small" /> รายละเอียดรูปแบบไฟล์ (CSV Format):
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1, pl: 1 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>ข้อมูลบังคับ:</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>รหัสนักศึกษา:</b> รหัส 9 หลักที่มีในระบบ</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>ชื่อโรงเรียน:</b> ต้องตรงกับชื่อในระบบจัดการโรงเรียน</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>รหัสวิชา:</b> ต้องตรงกับรหัสในระบบจัดการรายวิชา</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>ข้อมูลประกอบ:</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>Email อาจารย์วิชาชีพ:</b> ระบุผู้ประเมินด้านวิชาชีพครู</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>Email อาจารย์วิชาเอก:</b> ระบุผู้ประเมินด้านวิชาเอก</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>เทอม/ปีการศึกษา:</b> เช่น 1, 2569</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>สถานะ:</b> PENDING, ACTIVE, COMPLETED</Typography>
                </Grid>
            </Grid>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button 
                    size="small" 
                    variant="contained" 
                    color="primary" 
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadTemplate}
                >
                    ดาวน์โหลดไฟล์เทมเพลตการจัดสรร
                </Button>
            </Box>
          </Box>

          {importMessage.text && <Alert severity={importMessage.type as any} sx={{ mb: 2 }}>{importMessage.text}</Alert>}

          {importErrors.length > 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'error.50', borderRadius: 2, border: '1px solid', borderColor: 'error.200' }}>
              <Typography variant="subtitle2" color="error.main" gutterBottom sx={{ fontWeight: 'bold' }}>
                รายละเอียดข้อผิดพลาด ({importErrors.length} รายการ):
              </Typography>
              <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {importErrors.map((err, idx) => (
                  <Typography key={idx} variant="caption" sx={{ display: 'block', mb: 0.5, color: 'error.dark' }}>
                    • รหัส <b>{err.student_code}</b>: {err.reason}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ border: '2px dashed #ccc', p: 4, textAlign: 'center', borderRadius: 2, bgcolor: 'grey.50' }}>
            <input
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file-input"
              onChange={handleFileUpload}
            />
            <label htmlFor="csv-file-input">
              <Button variant="outlined" component="span" startIcon={<ImportIcon />} size="large">
                เลือกไฟล์ CSV จากเครื่องคอมพิวเตอร์
              </Button>
            </label>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
              คำแนะนำ: ระบบจะตรวจสอบความซ้ำซ้อนของข้อมูล (รหัสนักศึกษา+เทอม+ปี) โดยอัตโนมัติ
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>ปิดหน้าต่าง</Button>
        </DialogActions>
      </Dialog>

      {/* Manual Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{selectedPlacement ? 'ปรับปรุงข้อมูลการจัดสรร' : 'จัดสรรที่ฝึกประสบการณ์ใหม่'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={students}
                  getOptionLabel={(option: any) => `${option.student_code} - ${option.first_name} ${option.last_name}`}
                  value={students.find((s: any) => s.id === formData.student_id) || null}
                  onChange={(event, newValue: any) => {
                    setFormData({ ...formData, student_id: newValue ? newValue.id : '' });
                  }}
                  disabled={!!selectedPlacement}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="ค้นหานักศึกษา (ชื่อ หรือ รหัส)"
                      required
                      fullWidth
                    />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={courses}
                  getOptionLabel={(option: any) => `${option.course_code} - ${option.course_name}`}
                  value={courses.find((c: any) => c.id === formData.course_id) || null}
                  onChange={(event, newValue: any) => {
                    setFormData({ ...formData, course_id: newValue ? newValue.id : '' });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="ค้นหารายวิชา" required fullWidth />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={schools}
                  getOptionLabel={(option: any) => option.name}
                  value={schools.find((s: any) => s.id === formData.school_id) || null}
                  onChange={(event, newValue: any) => {
                    setFormData({ ...formData, school_id: newValue ? newValue.id : '', co_teacher_id: '' });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="ค้นหาสถานศึกษา" required fullWidth />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={supervisors.filter((s: any) => !!s.is_professional)}
                  getOptionLabel={(option: any) => `${option.prefix || ''}${option.first_name} ${option.last_name} (${option.department})`}
                  value={supervisors.find((s: any) => s.id === formData.prof_supervisor_id) || null}
                  onChange={(event, newValue: any) => {
                    setFormData({ ...formData, prof_supervisor_id: newValue ? newValue.id : '' });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="ค้นหาอาจารย์นิเทศก์ (วิชาชีพ)" fullWidth />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={supervisors.filter((s: any) => !!s.is_specialist)}
                  getOptionLabel={(option: any) => `${option.prefix || ''}${option.first_name} ${option.last_name} (${option.department})`}
                  value={supervisors.find((s: any) => s.id === formData.spec_supervisor_id) || null}
                  onChange={(event, newValue: any) => {
                    const id = newValue ? newValue.id : '';
                    setFormData({ ...formData, spec_supervisor_id: id });
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="อาจารย์นิเทศก์ (วิชาเอก)" 
                      fullWidth 
                    />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={coTeachers.filter((ct: any) => ct.school_id === formData.school_id)}
                  getOptionLabel={(option: any) => `${option.prefix || ''}${option.first_name} ${option.last_name}`}
                  value={coTeachers.find((ct: any) => ct.id === formData.co_teacher_id) || null}
                  onChange={(event, newValue: any) => {
                    setFormData({ ...formData, co_teacher_id: newValue ? newValue.id : '' });
                  }}
                  disabled={!formData.school_id}
                  renderInput={(params) => (
                    <TextField {...params} label="ค้นหาครูพี่เลี้ยง" fullWidth />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="เทอม"
                  name="semester"
                  value={formData.semester}
                  onChange={handleFormChange}
                >
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="ปีการศึกษา (พ.ศ.)"
                  name="academic_year"
                  type="number"
                  value={formData.academic_year}
                  onChange={handleFormChange}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label="สถานะ"
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                >
                  <MenuItem value="PENDING">PENDING (รอการยืนยัน)</MenuItem>
                  <MenuItem value="ACTIVE">ACTIVE (กำลังฝึกงาน)</MenuItem>
                  <MenuItem value="COMPLETED">COMPLETED (เสร็จสิ้นการฝึกงาน)</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)}>ยกเลิก</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            disabled={!formData.student_id || !formData.school_id || !formData.course_id}
          >
            บันทึกข้อมูล
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Course Dialog */}
      <Dialog open={bulkCourseOpen} onClose={() => setBulkCourseOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 'bold' }}>แก้ไขรายวิชาแบบกลุ่ม</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              ระบุรายวิชาที่จะจัดสรรให้กับนักศึกษาที่เลือกทั้งหมด <b>{selected.length} คน</b>
            </Typography>
            <Autocomplete
              options={courses}
              getOptionLabel={(option: any) => `${option.course_code} - ${option.course_name}`}
              value={courses.find((c: any) => c.id === bulkCourseId) || null}
              onChange={(event, newValue: any) => {
                setBulkCourseId(newValue ? newValue.id : '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="ค้นหารายวิชา" required fullWidth />
              )}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBulkCourseOpen(false)}>ยกเลิก</Button>
          <Button 
            onClick={handleBulkEditCourse} 
            variant="contained" 
            color="primary"
            disabled={!bulkCourseId}
          >
            บันทึกข้อมูล
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit School Dialog */}
      <Dialog open={bulkSchoolOpen} onClose={() => setBulkSchoolOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 'bold' }}>แก้ไขโรงเรียนแบบกลุ่ม</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              ระบุโรงเรียนที่จะจัดสรรให้กับนักศึกษาที่เลือกทั้งหมด <b>{selected.length} คน</b>
            </Typography>
            <Autocomplete
              options={schools}
              getOptionLabel={(option: any) => `${option.name} (${option.province || option.address || ''})`}
              value={schools.find((s: any) => s.id === bulkSchoolId) || null}
              onChange={(event, newValue: any) => {
                setBulkSchoolId(newValue ? newValue.id : '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="ค้นหาโรงเรียน" required fullWidth />
              )}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBulkSchoolOpen(false)}>ยกเลิก</Button>
          <Button 
            onClick={handleBulkEditSchool} 
            variant="contained" 
            color="primary"
            disabled={!bulkSchoolId}
          >
            บันทึกข้อมูล
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Cohort Allocation Dialog (จัดสรรตามหมู่เรียน) */}
      <Dialog open={sectionDialogOpen} onClose={() => setSectionDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid #E2E8F0' }}>
          จัดสรรรายวิชาและโรงเรียนตามหมู่เรียน (ทั้งหมู่เรียน)
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ mt: 0.5 }}>
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="ภาคเรียน *"
                  value={sectionSemester}
                  onChange={(e) => setSectionSemester(Number(e.target.value))}
                >
                  <MenuItem value={1}>ภาคเรียนที่ 1</MenuItem>
                  <MenuItem value={2}>ภาคเรียนที่ 2</MenuItem>
                  <MenuItem value={3}>ภาคเรียนที่ 3</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="ปีการศึกษา (พ.ศ.) *"
                  type="number"
                  value={sectionAcademicYear}
                  onChange={(e) => setSectionAcademicYear(Number(e.target.value))}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={uniqueGroupOptions}
                  getOptionLabel={(option: any) => typeof option === 'string' ? option : option.label}
                  value={uniqueGroupOptions.find((g: any) => g.group_no === sectionGroupNo) || null}
                  onChange={(event, newValue: any) => {
                    setSectionGroupNo(newValue ? newValue.group_no : '');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="เลือกหมู่เรียนที่ต้องการจัดสรร *" required fullWidth />
                  )}
                  fullWidth
                />
              </Grid>

              {/* Student Cohort Preview Badge */}
              {sectionGroupNo && (
                <Grid size={{ xs: 12 }}>
                  <Alert 
                    severity={studentsInSection.length > 0 ? "info" : "warning"} 
                    sx={{ borderRadius: 2 }}
                  >
                    {studentsInSection.length > 0 ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          พบนักศึกษาในหมู่เรียน {sectionGroupNo} ทั้งหมด {studentsInSection.length} คน
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                          ตัวอย่าง: {studentsInSection.slice(0, 3).map((s: any) => `${s.student_code} ${s.prefix || ''}${s.first_name}`).join(', ')}
                          {studentsInSection.length > 3 ? ` และอีก ${studentsInSection.length - 3} คน` : ''}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        ไม่พบข้อมูลนักศึกษาในหมู่เรียน {sectionGroupNo} ในระบบ
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={courses}
                  getOptionLabel={(option: any) => `${option.course_code} - ${option.course_name}`}
                  value={courses.find((c: any) => c.id === sectionCourseId) || null}
                  onChange={(event, newValue: any) => {
                    setSectionCourseId(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="กำหนดรายวิชาฝึกประสบการณ์ (Course)" 
                      helperText="เลือกรายวิชาที่นักศึกษาในหมู่เรียนนี้ต้องลงทะเบียนฝึกประสบการณ์"
                      fullWidth 
                    />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={schools}
                  getOptionLabel={(option: any) => `${option.name} (${option.province || option.address || ''})`}
                  value={schools.find((s: any) => s.id === sectionSchoolId) || null}
                  onChange={(event, newValue: any) => {
                    setSectionSchoolId(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="กำหนดโรงเรียนฝึกสอน (ทางเลือก - เว้นว่างได้)" 
                      helperText="หากทั้งหมู่เรียนฝึกสอนโรงเรียนเดียวกันสามารถระบุได้ทันที หรือเว้นว่างเพื่อจัดสรรภายหลัง"
                      fullWidth 
                    />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={supervisors.filter((s: any) => !!s.is_professional)}
                  getOptionLabel={(option: any) => `${option.prefix || ''}${option.first_name} ${option.last_name}`}
                  value={supervisors.find((s: any) => s.id === sectionProfSupId) || null}
                  onChange={(event, newValue: any) => {
                    setSectionProfSupId(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="อ.นิเทศก์ วิชาชีพครู (ทางเลือก)" 
                      fullWidth 
                    />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={supervisors.filter((s: any) => !!s.is_specialist)}
                  getOptionLabel={(option: any) => `${option.prefix || ''}${option.first_name} ${option.last_name} (${option.department})`}
                  value={supervisors.find((s: any) => s.id === sectionSpecSupId) || null}
                  onChange={(event, newValue: any) => {
                    setSectionSpecSupId(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="อ.นิเทศก์ เฉพาะสาขา (ทางเลือก)" 
                      fullWidth 
                    />
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setSectionDialogOpen(false)}>ยกเลิก</Button>
          <Button 
            onClick={handleBulkAllocateBySection} 
            variant="contained" 
            color="primary"
            disabled={!sectionGroupNo || (!sectionCourseId && !sectionSchoolId)}
            sx={{ fontWeight: 700 }}
          >
            บันทึกการจัดสรรทั้งหมู่เรียน {studentsInSection.length > 0 ? `(${studentsInSection.length} คน)` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Puzzle Verification Dialog for Auto-Match */}
      <PuzzleVerificationDialog
        open={puzzleOpen}
        onClose={() => setPuzzleOpen(false)}
        onConfirm={handleExecuteAutoMatch}
        semester={currentSemester}
        academicYear={currentAcademicYear}
        loading={autoMatching}
      />
    </Box>
  );
};

export default PlacementManager;
